import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { Box, Grid, Center, Text, useToast, Button } from '@chakra-ui/react';
import ImageBlog from 'components/common/ImageBlog';
import Container from 'components/common/Container';
import { getBlogListing } from 'services/blog';
import { getRealLocale } from 'utils/helpers';
import BlogMenu from 'components/blog/BlogMenu';
import FadeIn from 'components/transitions/FadeIn';
import useTranslation from 'next-translate/useTranslation';
import BreadCrumb from 'components/common/BreadCrumb';
import useBlog from 'hooks/useBlog';
import useBlogCategories from 'hooks/useBlogCategories';
import MetaTagsHandler from '../common/MetaTagsHandler';
import Head from 'next/head';

export default function BlogCategoryView({ data, category, categoryId }) {
  const { t } = useTranslation('common');

  const router = useRouter();
  const toast = useToast();

  const { blogData, mutate } = useBlog(data, 0, categoryId);
  const { categories } = useBlogCategories(category, 0);
  const [loadingMore, setLoadingMore] = useState(false);
  const firstElement = blogData?.results[0];
  const secondElement = blogData?.results[1];
  const thirdElement = blogData?.results[2];
  const [breadcrumbs, setBreadcrumbs] = useState([
    {
      name: 'Home',
      path: '/',
    },
    {
      name: 'Blog',
      path: `/${getRealLocale(router.locale)}/blog`,
    },
  ]);

  useEffect(() => {
    if (categoryId && breadcrumbs.length === 2 && categories && categories.results && categories.results.length > 0) {
      const selectedCategory = categories.results.find((i) => i.id === categoryId);
      setBreadcrumbs([
        ...breadcrumbs,
        { name: selectedCategory.name, path: `/${getRealLocale(router.locale)}${selectedCategory.path}` },
      ]);
    }
  }, [categories, categoryId]);

  const handleLoadMore = () => {
    setLoadingMore(true);
    getBlogListing(router.locale, categoryId, Number(blogData.pager.currentpage) + 1)
      .then((res) => {
        mutate(
          {
            ...blogData,
            results: blogData.results.concat(res.data.results),
            pager: res.data.pager,
          },
          false
        );
      })
      .catch((err) => {
        toast({
          description: t('errorLoadingMoreItems'),
          position: 'top',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      })
      .finally(() => {
        setLoadingMore(false);
      });
  };

  return (
    <FadeIn>
      {blogData?.metaTags && <MetaTagsHandler metaTags={blogData?.metaTags} />}
      <Head>
        <title>Welcome to AMM blog</title>
      </Head>
      <Box pos="relative" marginBottom="30px">
        <Box
          backgroundColor={{ base: 'white', lg: 'brand.900' }}
          display="flex"
          pos={{ base: 'relative', lg: 'absolute' }}
          left={{ base: '0', lg: '20px' }}
          top={{ base: '0', lg: '10px' }}
          fontSize="12px"
          lineHeight="15px"
          fontWeight="bold"
          color={{ base: 'black', lg: 'white' }}
          padding={{ base: '15px', lg: '0' }}
        >
          <BreadCrumb breadcrumbs={breadcrumbs} white={true} />
        </Box>
        <Center w="100%" h={{ base: '120px', md: '160px', lg: '200px' }} backgroundColor="brand.900" marginBottom="-1">
          <Text
            fontSize={{ base: '26px', md: '32px', lg: '36px' }}
            lineHeight={{ base: '28px', md: '34px', lg: '45px' }}
            color="white"
          >
            {t('blog:welcome')}
            <Box as={'span'} fontWeight="bold">
              {' '}
              AMM
            </Box>{' '}
            blog
          </Text>
        </Center>
        <BlogMenu category={categories?.results} />
      </Box>
      <Container marginBottom="75px">
        <Box display={{ base: 'block', lg: 'flex' }}>
          <Box w={{ base: '100%', lg: '70%' }} paddingRight={{ base: '0', lg: '35px' }}>
            <ImageBlog
              big={true}
              title={firstElement?.title}
              blogPath={firstElement?.blogPath}
              body={firstElement?.body}
              categoryName={firstElement?.categoryName}
              categoryPath={firstElement?.categoryPath}
              created={firstElement?.created}
              image={firstElement?.image}
            />
          </Box>
          <Grid
            templateColumns={{ base: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', lg: `repeat(1, 1fr)` }}
            gap={6}
            w={{ base: '100%', lg: '30%' }}
            display={{ base: 'block', sm: 'flex', lg: 'block' }}
          >
            <ImageBlog
              title={secondElement?.title}
              blogPath={secondElement?.blogPath}
              body={secondElement?.body}
              categoryName={secondElement?.categoryName}
              categoryPath={secondElement?.categoryPath}
              created={secondElement?.created}
              image={secondElement?.image}
            />
            <ImageBlog
              title={thirdElement?.title}
              blogPath={thirdElement?.blogPath}
              body={thirdElement?.body}
              categoryName={thirdElement?.categoryName}
              categoryPath={thirdElement?.categoryPath}
              created={thirdElement?.created}
              image={thirdElement?.image}
            />
          </Grid>
        </Box>
        <Grid templateColumns={{ base: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', lg: `repeat(3, 1fr)` }} gap={6}>
          {blogData?.results?.map(({ title, blogPath, body, categoryName, categoryPath, created, image }, index) => {
            if (index > 2) {
              return (
                <ImageBlog
                  key={`blogItem-${index}`}
                  title={title}
                  blogPath={blogPath}
                  body={body}
                  categoryName={categoryName}
                  categoryPath={categoryPath}
                  created={created}
                  image={image}
                />
              );
            }
          })}
        </Grid>

        {blogData?.pager?.hasNext ? (
          <Box as="div" textAlign={'center'} my={'50px'}>
            <Button
              variant={'secondary'}
              isLoading={loadingMore}
              minW={{ base: '100%', sm: '350px' }}
              maxW={'100%'}
              onClick={handleLoadMore}
            >
              {t('loadMore')}
            </Button>
          </Box>
        ) : null}
      </Container>
    </FadeIn>
  );
}
